import { ArrowLeft, CheckCircle2, Clock3, Code2, FileQuestion, Gauge, Loader2, Play, Send, Sparkles, Target } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  getLearningPractice,
  runLearningPractice,
  runLearningPracticeTests,
  submitLearningPractice,
  updateAiGrowthTask,
} from '../../api.js'

const defaultStarterCode = `public class Main {
    public static void main(String[] args) {
        System.out.println("Hello, practice!");
    }
}`

function fallbackPractice(practiceId) {
  return {
    id: practiceId,
    title: 'Java 基础练习',
    type: 'code',
    language: 'Java',
    difficulty: '基础',
    estimatedMinutes: 20,
    question: '用 Java 写一个最小可运行程序，输出 Hello Java，并补充输入、输出和边界情况说明。',
    inputDescription: '',
    outputDescription: 'Hello Java',
    sampleInput: '',
    sampleOutput: 'Hello Java',
    knowledgePoints: ['Java 程序入口', '标准输出', '基础语法'],
    starterCode: defaultStarterCode,
    nodeTitle: 'Java 基础',
    status: 'pending',
    bestScore: 0,
    attempts: 0,
    latestSubmission: null,
    testCases: [],
  }
}

function pick(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '')
}

function asList(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map(String)
  if (typeof value === 'string' && value.trim()) return value.split(/[、\n]/).map((item) => item.trim()).filter(Boolean)
  return []
}

function normalizePractice(raw, practiceId) {
  const fallback = fallbackPractice(practiceId)
  const practice = raw && typeof raw === 'object' ? raw : {}
  const testCases = Array.isArray(practice.testCases) ? practice.testCases : []
  const firstVisibleCase = testCases.find((item) => !item.hidden && !item.isHidden) || {}
  return {
    ...fallback,
    ...practice,
    id: pick(practice.id, practiceId),
    title: pick(practice.title, fallback.title),
    type: pick(practice.type, fallback.type),
    language: pick(practice.language, fallback.language),
    difficulty: pick(practice.difficulty, fallback.difficulty),
    estimatedMinutes: Number(pick(practice.estimatedMinutes, practice.estimated_minutes, fallback.estimatedMinutes)),
    question: pick(practice.question, practice.requirement, practice.description, fallback.question),
    inputDescription: pick(practice.inputDescription, practice.input_description, practice.inputSpec, firstVisibleCase.input),
    outputDescription: pick(practice.outputDescription, practice.output_description, practice.outputSpec, firstVisibleCase.expectedOutput, fallback.outputDescription),
    sampleInput: pick(practice.sampleInput, practice.sample_input, firstVisibleCase.input),
    sampleOutput: pick(practice.sampleOutput, practice.sample_output, firstVisibleCase.expectedOutput, fallback.sampleOutput),
    knowledgePoints: asList(practice.knowledgePoints || practice.knowledge_points),
    starterCode: pick(practice.starterCode, practice.starter_code, fallback.starterCode),
    testCases,
  }
}

function runLanguage(language) {
  const key = String(language || '').toLowerCase()
  if (key.includes('python')) return 'python'
  if (key.includes('javascript') || key.includes('node')) return 'javascript'
  if (key.includes('java')) return 'java'
  return ''
}

function fileExtension(language) {
  return {
    java: 'java',
    python: 'py',
    javascript: 'js',
  }[runLanguage(language)] || 'txt'
}

function typeLabel(type) {
  return {
    code: '代码练习',
    short_answer: '简答练习',
    project_thinking: '项目拆解练习',
    interview_question: '面试练习',
  }[type] || '练习'
}

function normalizeFeedback(value) {
  if (!value || typeof value !== 'object') return null
  const score = Number(value.score || 0)
  return {
    score: Number.isFinite(score) ? score : 0,
    passed: value.passed === true || score >= 60,
    summary: String(value.summary || value.completion || '已生成练习反馈。'),
    problems: Array.isArray(value.problems) ? value.problems : Array.isArray(value.issues) ? value.issues : [],
    suggestions: Array.isArray(value.suggestions) ? value.suggestions : [],
    referenceAnswer: String(value.referenceAnswer || value.reference_answer || ''),
  }
}

function friendlyRunnerError(message) {
  const text = String(message || '')
  if (/spawn\s+javac\s+ENOENT/i.test(text)) {
    return 'Java 运行环境未启动：服务器没有找到 JDK，请配置 JAVA_HOME 或把 JDK bin 目录加入 PATH。'
  }
  return text
}

function buildPracticeGuide(practice, visibleCases) {
  const title = practice.nodeTitle || practice.title || '当前知识点'
  const expectedOutput = practice.sampleOutput || visibleCases[0]?.expectedOutput || practice.outputDescription || '按题目要求输出结果'
  return {
    goal: `完成一个和「${title}」相关的最小可运行练习，并让程序输出符合要求的结果。`,
    requirements: [
      runLanguage(practice.language) === 'java' ? '保留 class Main，并在 main 方法中完成代码。' : '在编辑器中写出可直接运行的代码。',
      practice.inputDescription ? `按输入说明处理数据：${practice.inputDescription}` : '本题没有固定输入时，可以先完成固定输出，再思考可扩展输入。',
      `输出结果需要匹配：${expectedOutput}`,
      '提交前至少检查一次语法、输出格式和边界情况。',
    ],
    steps: [
      '先读清题目目标，不急着写完整答案。',
      '写出最小可运行版本，保证程序能启动。',
      '点击运行查看实际输出，再对照示例输出调整。',
      '通过后再补充边界处理或更清晰的变量命名。',
    ],
    analysis: [
      '这类基础题的关键不是代码量，而是入口、输出和格式是否准确。',
      '如果运行失败，先看编译错误或运行器提示，再检查类名、括号和输出语句。',
      '如果输出不一致，优先检查大小写、空格、换行和多余字符。',
    ],
  }
}

function FeedbackPanel({ feedback }) {
  if (!feedback) {
    return (
      <article className="learning-practice-feedback">
        <span><Sparkles size={16} /> AI 反馈</span>
        <h2>提交后查看评分和建议</h2>
        <p>完成后提交，系统会给出得分、问题、优化建议和参考答案。</p>
      </article>
    )
  }
  return (
    <article className="learning-practice-feedback">
      <span><Sparkles size={16} /> AI 反馈</span>
      <h2>{feedback.score} 分 · {feedback.passed ? '已通过' : '未通过'}</h2>
      <p>{feedback.summary}</p>
      <section>
        <h3>问题</h3>
        {feedback.problems.length ? <ul>{feedback.problems.map((item) => <li key={item}>{item}</li>)}</ul> : <p>暂无明显问题。</p>}
      </section>
      <section>
        <h3>优化建议</h3>
        {feedback.suggestions.length ? <ul>{feedback.suggestions.map((item) => <li key={item}>{item}</li>)}</ul> : <p>继续保持当前解题习惯，提交前再检查边界情况。</p>}
      </section>
      <section>
        <h3>参考答案</h3>
        <p>{feedback.referenceAnswer || '暂无参考答案。建议先完成最小可运行版本，再补充边界情况和说明。'}</p>
      </section>
    </article>
  )
}

function ResultBox({ title, result }) {
  if (!result) return null
  const stderr = friendlyRunnerError(result.stderr)
  return (
    <section className={`practice-run-result ${result.ok ? 'is-success' : 'is-error'}`}>
      <div className="practice-run-result-header">
        <strong>{title}</strong>
        <span>{result.ok ? '成功' : '失败'}</span>
      </div>
      {result.score !== undefined ? <p>得分：{result.score}</p> : null}
      {result.stdout ? <pre>{result.stdout}</pre> : null}
      {stderr ? <pre>{stderr}</pre> : null}
    </section>
  )
}

function LearningPracticePage() {
  const { practiceId, id } = useParams()
  const [searchParams] = useSearchParams()
  const fromAiNode = searchParams.get('fromAiNode') || ''
  const aiTaskId = searchParams.get('aiTaskId') || ''
  const currentId = practiceId || id
  const [practice, setPractice] = useState(() => fallbackPractice(currentId))
  const [answer, setAnswer] = useState('')
  const [runResult, setRunResult] = useState(null)
  const [testResult, setTestResult] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [testing, setTesting] = useState(false)
  const [analysisOpen, setAnalysisOpen] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    getLearningPractice(currentId, controller.signal)
      .then((payload) => {
        const nextPractice = normalizePractice(payload?.practice, currentId)
        setPractice(nextPractice)
        setFeedback(normalizeFeedback(nextPractice.latestSubmission?.feedback || nextPractice.latestSubmission?.feedbackJson))
        setAnswer(nextPractice.latestSubmission?.answer || nextPractice.starterCode || defaultStarterCode)
        setRunResult(null)
        setTestResult(null)
        setAnalysisOpen(false)
        setError('')
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        const fallback = fallbackPractice(currentId)
        setPractice(fallback)
        setAnswer(fallback.starterCode)
        setFeedback(null)
        setRunResult(null)
        setTestResult(null)
        setAnalysisOpen(false)
        setError(err.message || '练习暂时加载失败，已显示默认题目。')
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [currentId])

  const completed = practice.status === 'completed' || Boolean(practice.completedAt)
  const statusText = completed ? '已完成' : Number(practice.attempts || 0) > 0 ? '已提交，继续优化' : '待完成'
  const canRunCode = practice.type === 'code' && Boolean(runLanguage(practice.language))
  const knowledgePoints = practice.knowledgePoints.length ? practice.knowledgePoints : [practice.nodeTitle || '当前知识点']
  const lineNumbers = answer.split('\n').slice(0, 120)
  const visibleCases = useMemo(
    () => (Array.isArray(practice.testCases) ? practice.testCases.filter((item) => !item.hidden && !item.isHidden) : []),
    [practice.testCases],
  )
  const guide = useMemo(() => buildPracticeGuide(practice, visibleCases), [practice, visibleCases])

  async function handleRunCode() {
    if (!canRunCode || !answer.trim()) return
    setRunning(true)
    setRunResult(null)
    setAnalysisOpen(true)
    try {
      setRunResult(await runLearningPractice(practice.id, { code: answer, language: runLanguage(practice.language), stdin: '' }))
    } catch (err) {
      setRunResult({ ok: false, stdout: '', stderr: friendlyRunnerError(err.message) || '运行失败，请登录后重试。' })
    } finally {
      setRunning(false)
    }
  }

  async function handleRunTests() {
    if (!canRunCode || !answer.trim()) return
    setTesting(true)
    setTestResult(null)
    setAnalysisOpen(true)
    try {
      setTestResult(await runLearningPracticeTests(practice.id, { code: answer, language: runLanguage(practice.language) }))
    } catch (err) {
      setTestResult({ ok: false, score: 0, stderr: friendlyRunnerError(err.message) || '运行测试失败，请登录后重试。' })
    } finally {
      setTesting(false)
    }
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    setNotice('')
    setAnalysisOpen(true)
    try {
      const payload = await submitLearningPractice(practice.id, { answer, language: practice.language || 'Java', type: practice.type || 'code' })
      const nextFeedback = normalizeFeedback(payload?.submission?.feedback || payload)
      setFeedback(nextFeedback)
      setPractice({
        ...practice,
        status: payload?.passed || nextFeedback?.passed ? 'completed' : practice.status,
        bestScore: Math.max(Number(practice.bestScore || 0), Number(payload?.score || nextFeedback?.score || 0)),
        attempts: Number(practice.attempts || 0) + 1,
        completedAt: payload?.passed || nextFeedback?.passed ? new Date().toISOString() : practice.completedAt,
      })
      setNotice(payload?.message || (payload?.passed || nextFeedback?.passed ? '已通过练习，成长记录已更新。' : '本次未通过，可以修改后再次提交。'))
      if ((payload?.passed || nextFeedback?.passed) && aiTaskId) {
        await updateAiGrowthTask(aiTaskId, { action: 'complete', note: `代码练习已通过：${practice.title}` }).catch(() => {})
      }
    } catch (err) {
      setError(err.message || '提交失败，请登录后重试。')
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="page-container student-roadmap-page learning-practice-page">
      <section className="student-roadmap-stage-toolbar">
        <Link className="secondary-button" to={fromAiNode ? `/learning/ai/${encodeURIComponent(fromAiNode)}` : practice.nodeId ? `/learning/${practice.nodeId}` : '/learning'}>
          <ArrowLeft size={16} /> 返回学习节点
        </Link>
      </section>

      {error ? <p className="student-growth-error">{error}</p> : null}
      {notice ? <p className="student-growth-error is-success">{notice}</p> : null}

      <section className="practice-workbench" aria-label="在线刷题工作台">
        <article className="practice-workbench-panel practice-problem-pane">
          <header className="practice-problem-top">
            <span><FileQuestion size={16} /> 在线练习 · {practice.language || 'Java'}</span>
            <h1>{loading ? '正在加载练习...' : practice.title}</h1>
            <div className="practice-chip-row">
              <small><Target size={14} /> {practice.nodeTitle || '当前知识点'}</small>
              <small><Gauge size={14} /> {practice.difficulty || '基础'}</small>
              <small><Clock3 size={14} /> {practice.estimatedMinutes || 20} 分钟</small>
              <small><CheckCircle2 size={14} /> {statusText}</small>
            </div>
          </header>

          <div className="practice-problem-scroll">
            <section className="practice-layer-card">
              <div className="practice-layer-heading">
                <span>01</span>
                <div>
                  <strong>题目</strong>
                  <p>{guide.goal}</p>
                </div>
              </div>
              <article className="practice-problem-section is-main">
                <span>题目描述</span>
                <p>{practice.question}</p>
              </article>
              <ul className="practice-requirement-list">
                {guide.requirements.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </section>

            <section className="practice-layer-card">
              <div className="practice-layer-heading">
                <span>02</span>
                <div>
                  <strong>练习</strong>
                  <p>按输入、输出和示例完成右侧代码。</p>
                </div>
              </div>
              <div className="practice-problem-grid">
                <article className="practice-problem-section"><span>输入说明</span><p>{practice.inputDescription || '暂无固定输入要求。'}</p></article>
                <article className="practice-problem-section"><span>输出说明</span><p>{practice.outputDescription || '请输出能体现解题结果的内容。'}</p></article>
                <article className="practice-problem-section"><span>示例输入</span><pre>{practice.sampleInput || visibleCases[0]?.input || '无示例输入'}</pre></article>
                <article className="practice-problem-section"><span>示例输出</span><pre>{practice.sampleOutput || visibleCases[0]?.expectedOutput || '无示例输出'}</pre></article>
              </div>
              <ol className="practice-step-list">
                {guide.steps.map((item) => <li key={item}>{item}</li>)}
              </ol>
            </section>

            <section className={`practice-layer-card practice-analysis-card ${analysisOpen ? 'is-open' : ''}`}>
              <button className="practice-analysis-toggle" type="button" onClick={() => setAnalysisOpen((value) => !value)}>
                <span>03</span>
                <div>
                  <strong>解析</strong>
                  <p>{analysisOpen ? '运行后自动展开，可随时收起。' : '默认收起，先完成练习再看提示。'}</p>
                </div>
                <small>{analysisOpen ? '收起' : '展开'}</small>
              </button>
              {analysisOpen ? (
                <div className="practice-analysis-body">
                  <ul>
                    {guide.analysis.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                  <div className="practice-point-list">
                    {knowledgePoints.map((item) => <small key={item}>{item}</small>)}
                  </div>
                  <p>最高分 {practice.bestScore || 0} · 已提交 {practice.attempts || 0} 次 · {typeLabel(practice.type)}</p>
                </div>
              ) : null}
            </section>
          </div>
        </article>

        <form className="practice-workbench-panel practice-solve-pane" onSubmit={handleSubmit}>
          <header className="practice-solve-toolbar">
            <div>
              <span><Code2 size={16} /> {canRunCode ? '代码编辑器' : '作答区'}</span>
              <strong>{practice.language || 'Text'}</strong>
            </div>
            <div className="practice-editor-actions">
              <button className="secondary-button" type="button" onClick={handleRunCode} disabled={!canRunCode || running || !answer.trim()}>
                {running ? <Loader2 size={16} /> : <Play size={16} />}
                运行
              </button>
              <button className="secondary-button" type="button" onClick={handleRunTests} disabled={!canRunCode || testing || !answer.trim()}>
                {testing ? <Loader2 size={16} /> : <CheckCircle2 size={16} />}
                测试
              </button>
              <button className="primary-button" type="submit" disabled={saving || !answer.trim()}>
                {saving ? <Loader2 size={16} /> : <Send size={16} />}
                提交
              </button>
            </div>
          </header>

          <section className="practice-editor-chrome" aria-label="代码编辑器">
            <div className="practice-editor-tabs">
              <span><Code2 size={14} /> Main.{fileExtension(practice.language)}</span>
              <small>{lineNumbers.length} 行</small>
            </div>
            <div className="practice-code-shell">
              <div className="practice-code-gutter" aria-hidden="true">
                {lineNumbers.map((_, index) => <span key={`line-${index + 1}`}>{index + 1}</span>)}
              </div>
              <textarea
                aria-label="代码答案"
                className="practice-code-textarea"
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
                spellCheck={false}
              />
            </div>
          </section>

          <section className="practice-result-dock" aria-label="运行和反馈结果">
            <ResultBox title="运行结果" result={runResult} />
            <ResultBox title="测试结果" result={testResult} />
            <FeedbackPanel feedback={feedback} />
          </section>
        </form>
      </section>
    </main>
  )
}

export default LearningPracticePage
